import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Button,
  Chip
} from "@mui/material";
import { Receipt, Share } from "@mui/icons-material";
import { getAccountsList } from "../firebase/dataHandler";

function Invoices() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    getAccountsList()
      .then((accountList) => {
        let allEntries = [];
        Object.values(accountList).forEach((account) => {
          if (account.entries) {
            Object.entries(account.entries).forEach(([entryId, entry]) => {
              allEntries.push({
                ...entry,
                entryId,
                accountId: account.accountId,
                accountName: account.name,
                installmentAmount: account.installmentAmount,
                dateObj: new Date(entry.date)
              });
            });
          }
        });
        // Sort newest first
        allEntries.sort((a, b) => b.dateObj - a.dateObj);
        setEntries(allEntries); 
        setLoading(false);
      })
      .catch((e) => {
        alert("Failed to load invoices.");
        setLoading(false);
      });
  }, []);

  const currencyFormat = (val) => Number(val).toLocaleString("en-IN", { maximumFractionDigits: 3 });

  const handleShare = (accountId, entryId, amount) => {
    const url = `${window.location.origin}/invoice/v/${accountId}/${entryId}`;
    const message = `Hello, your payment of Rs ${currencyFormat(amount)} is received. View receipt: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="page-pad fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 4px 16px" }}>
        <div>
          <h1 style={{ fontSize: 28, marginBottom: 4 }}>Invoices & Receipts</h1>
          <small style={{ color: "var(--text-muted)", fontSize: 12 }}>Recent payment receipts generated for your customers</small>
        </div>
      </div>

      <div style={{ padding: "0 4px" }}>
        {loading ? (
          <div style={{ color: "var(--text-secondary)", padding: 20 }}>Loading invoices...</div>
        ) : (
          <TableContainer
            component={Paper}
            style={{
              background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: 16, boxShadow: "none",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow style={{ background: "var(--bg-elevated)" }}>
                  <TableCell style={{ color: "var(--text-secondary)", fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 600, borderBottom: "1px solid var(--border-subtle)", padding: "12px" }}>Date</TableCell>
                  <TableCell style={{ color: "var(--text-secondary)", fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 600, borderBottom: "1px solid var(--border-subtle)", padding: "12px" }}>Customer</TableCell>
                  <TableCell style={{ color: "var(--text-secondary)", fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 600, borderBottom: "1px solid var(--border-subtle)", padding: "12px" }}>Amount Paid</TableCell>
                  <TableCell style={{ color: "var(--text-secondary)", fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 600, borderBottom: "1px solid var(--border-subtle)", padding: "12px" }}>Status</TableCell>
                  <TableCell align="right" style={{ color: "var(--text-secondary)", fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 600, borderBottom: "1px solid var(--border-subtle)", padding: "12px" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" style={{ padding: 20, color: "var(--text-muted)", borderBottom: 'none' }}>
                      No receipts found.
                    </TableCell>
                  </TableRow>
                ) : (
                  (rowsPerPage > 0
                    ? entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    : entries
                  ).map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell style={{ color: "var(--text-primary)", fontFamily: "Inter, sans-serif", fontSize: 13, borderBottom: "1px solid var(--border-subtle)", padding: "12px" }}>
                        {new Date(row.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell style={{ fontFamily: "Inter, sans-serif", fontSize: 13, borderBottom: "1px solid var(--border-subtle)", padding: "12px" }}>
                        <Link to={`/account/${row.accountId}`} style={{ color: "var(--accent-blue)", textDecoration: "none", fontWeight: 600 }}>
                          {row.accountName}
                        </Link>
                      </TableCell>
                      <TableCell style={{ color: "var(--text-primary)", fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, borderBottom: "1px solid var(--border-subtle)", padding: "12px" }}>
                        ₹ {currencyFormat(row.amountPaid)}
                      </TableCell>
                      <TableCell style={{ borderBottom: "1px solid var(--border-subtle)", padding: "12px" }}>
                        <Chip label="Paid" size="small" style={{ background: "rgba(76, 175, 80, 0.1)", color: "#4caf50", fontWeight: 600, fontSize: 11 }} />
                      </TableCell>
                      <TableCell align="right" style={{ borderBottom: "1px solid var(--border-subtle)", padding: "12px" }}>
                        <Button
                          component={Link}
                          to={`/invoice/v/${row.accountId}/${row.entryId}`}
                          target="_blank"
                          size="small"
                          startIcon={<Receipt />}
                          style={{ color: "var(--text-primary)", textTransform: "none", fontSize: 12, marginRight: 8 }}
                        >
                          View
                        </Button>
                        <Button
                          onClick={() => handleShare(row.accountId, row.entryId, row.amountPaid)}
                          size="small"
                          startIcon={<Share />}
                          style={{ background: "rgba(21, 195, 88, 0.15)", color: "var(--accent-green)", textTransform: "none", fontSize: 12, borderRadius: 8, padding: "4px 12px" }}
                        >
                          Share
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, { label: "All", value: -1 }]}
                    colSpan={5}
                    count={entries.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setPage(0);
                    }}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    style={{ color: "var(--text-secondary)", fontFamily: "Inter, sans-serif", borderBottom: "none" }}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </div>
    </div>
  );
}

export default Invoices;
